import { motion } from 'framer-motion';

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 28 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] },
});

export default function Hero() {
  return (
    <section
      data-testid="hero-section"
      className="relative min-h-screen flex items-center justify-center overflow-hidden bg-[#FAFAF7]"
    >
      {/* Animated gradient blob */}
      <motion.div
        aria-hidden="true"
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] rounded-full pointer-events-none"
        style={{
          background: 'radial-gradient(circle at 40% 50%, rgba(0,212,184,0.18) 0%, rgba(10,22,40,0.06) 60%, transparent 100%)',
          filter: 'blur(80px)',
        }}
        animate={{
          scale: [1, 1.08, 1.02, 1],
          borderRadius: ['60% 40% 55% 45%', '45% 55% 40% 60%', '55% 45% 60% 40%', '60% 40% 55% 45%'],
        }}
        transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut' }}
      />

      {/* Secondary blob */}
      <motion.div
        aria-hidden="true"
        className="absolute top-1/4 right-1/4 w-[400px] h-[400px] rounded-full pointer-events-none"
        style={{
          background: 'radial-gradient(circle, rgba(0,212,184,0.10) 0%, transparent 70%)',
          filter: 'blur(60px)',
        }}
        animate={{
          x: [0, 30, -15, 0],
          y: [0, -20, 10, 0],
        }}
        transition={{ duration: 14, repeat: Infinity, ease: 'easeInOut' }}
      />

      <div className="relative z-10 max-w-5xl mx-auto px-6 lg:px-10 text-center pt-24 pb-16">
        <motion.p
          {...fadeUp(0.1)}
          className="text-xs font-bold uppercase tracking-[0.2em] text-[#0A1628]/50 mb-8"
        >
          Customer Retention as a Service
        </motion.p>

        <motion.h1
          {...fadeUp(0.2)}
          data-testid="hero-headline"
          className="text-5xl sm:text-6xl lg:text-7xl font-light tracking-tighter text-[#1A1A1A] leading-[1.05] mb-8"
        >
          Stop losing customers
          <br />
          <span className="font-bold text-[#0A1628]">who try to cancel</span>
        </motion.h1>

        <motion.p
          {...fadeUp(0.35)}
          data-testid="hero-subheadline"
          className="text-base md:text-xl font-normal leading-relaxed text-[#1A1A1A]/65 max-w-2xl mx-auto mb-12"
        >
          Specialist retention service for online subscription businesses. We turn
          cancellation attempts into retained revenue.
        </motion.p>

        <motion.div
          {...fadeUp(0.48)}
          className="flex flex-col sm:flex-row gap-4 justify-center mb-8"
        >
          <a
            href="#book"
            data-testid="hero-primary-cta"
            className="bg-[#0A1628] text-[#FAFAF7] font-semibold px-8 py-4 rounded-2xl hover:scale-[1.03] hover:bg-[#0A1628]/90 transition-all duration-200 shadow-lg text-base"
          >
            Book a 15 minute call
          </a>
          <a
            href="#how-it-works"
            data-testid="hero-secondary-cta"
            className="border-2 border-[#0A1628]/20 text-[#0A1628] font-semibold px-8 py-4 rounded-2xl hover:scale-[1.03] hover:border-[#0A1628]/40 hover:bg-[#0A1628]/5 transition-all duration-200 text-base"
          >
            See how it works
          </a>
        </motion.div>

        <motion.p
          {...fadeUp(0.58)}
          data-testid="hero-guarantee-text"
          className="text-sm text-[#1A1A1A]/40 tracking-wide"
        >
          30 day money back guarantee · No long contracts
        </motion.p>
      </div>
    </section>
  );
}
